import React, { useState, useEffect } from 'react';
import { getAuth, updateProfile, updateEmail, reauthenticateWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { authService } from '../services/authService';
import type { User, StrictUserUpdateFormData } from '../types/user';
import { Modal } from '../components/common/Modal';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { toast } from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';

export const ProfilePage: React.FC = () => {
  const { user, loading } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  
  // 프로필 폼 데이터
  const [profileForm, setProfileForm] = useState<StrictUserUpdateFormData>({
    displayName: '',
    email: '',
    role: 'viewer'
  });
  
  // 비밀번호 변경 폼 데이터
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // 현재 사용자 정보 가져오기
  useEffect(() => {
    if (user) {
      setProfileForm({
        displayName: user.displayName || '',
        email: user.email || '',
        role: 'viewer'
      });
    }
  }, [user]);

  // 프로필 업데이트 핸들러
  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) return;

    try {
      const auth = getAuth();
      const firebaseUser = auth.currentUser;
      
      if (!firebaseUser) {
        throw new Error('사용자를 찾을 수 없습니다.');
      }

      // 이름 업데이트
      if (profileForm.displayName && profileForm.displayName !== firebaseUser.displayName) {
        await updateProfile(firebaseUser, {
          displayName: profileForm.displayName
        });
      }

      // 이메일 업데이트
      if (profileForm.email && profileForm.email !== firebaseUser.email) {
        await updateEmail(firebaseUser, profileForm.email);
      }

      toast.success('프로필이 성공적으로 업데이트되었습니다.');
      setIsEditing(false);
    } catch (error) {
      console.error('프로필 업데이트 중 오류 발생:', error);
      toast.error('프로필 업데이트에 실패했습니다.');
    }
  };

  // 비밀번호 변경 핸들러
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('새 비밀번호가 일치하지 않습니다.');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error('비밀번호는 6자 이상이어야 합니다.');
      return;
    }

    try {
      // 여기에 실제 비밀번호 변경 API 호출 로직 구현
      console.log('비밀번호 변경:', passwordForm);
      
      // 임시로 성공 처리 (실제로는 Firebase 비밀번호 변경 필요)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success('비밀번호가 성공적으로 변경되었습니다.');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setIsPasswordModalOpen(false);
    } catch (error) {
      console.error('비밀번호 변경 중 오류 발생:', error);
      toast.error('비밀번호 변경에 실패했습니다.');
    }
  };

  // 회원탈퇴 핸들러
  const handleAccountDelete = async () => {
    if (!user) return;

    try {
      // 여기에 실제 회원탈퇴 API 호출 로직 구현
      console.log('회원탈퇴:', user.uid);
      
      // 임시로 성공 처리 (실제로는 Firebase 계정 삭제 필요)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success('회원탈퇴가 완료되었습니다.');
      setIsDeleteModalOpen(false);
      authService.signOutUser();
    } catch (error) {
      console.error('회원탈퇴 중 오류 발생:', error);
      toast.error('회원탈퇴에 실패했습니다.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">로그인이 필요합니다</h2>
        <p className="text-gray-600">프로필을 보려면 먼저 로그인해주세요.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">프로필</h1>
        <p className="text-gray-600">개인 정보를 관리하고 설정을 변경할 수 있습니다.</p>
      </div>

      {/* 프로필 정보 카드 */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center mb-6">
          <div className="w-20 h-20 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center mr-4">
            {user.displayName ? (
              <span className="text-2xl font-bold text-gray-600 dark:text-gray-300">
                {user.displayName.charAt(0).toUpperCase()}
              </span>
            ) : (
              <span className="text-2xl font-bold text-gray-600 dark:text-gray-300">U</span>
            )}
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
              {user.displayName || '이름 없음'}
            </h2>
            <p className="text-gray-600 dark:text-gray-300">{user.email}</p>
            <span className="inline-block mt-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
              {user.role}
            </span>
          </div>
        </div>

        {/* 프로필 수정 버튼 */}
        <div className="flex space-x-3">
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            프로필 수정
          </button>
          <button
            onClick={() => setIsPasswordModalOpen(true)}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            비밀번호 변경
          </button>
          <button
            onClick={() => setIsDeleteModalOpen(true)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            회원탈퇴
          </button>
        </div>
      </div>

      {/* 프로필 수정 모달 */}
      <Modal
        isOpen={isEditing}
        onClose={() => setIsEditing(false)}
        title="프로필 수정"
        size="md"
      >
        <form onSubmit={handleProfileUpdate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              이름
            </label>
            <input
              type="text"
              value={profileForm.displayName}
              onChange={(e) => setProfileForm({...profileForm, displayName: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="이름을 입력하세요"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              이메일
            </label>
            <input
              type="email"
              value={profileForm.email}
              onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="이메일을 입력하세요"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              이메일을 변경하려면 현재 비밀번호로 재인증이 필요합니다.
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white transition-colors"
            >
              취소
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              저장
            </button>
          </div>
        </form>
      </Modal>

      {/* 비밀번호 변경 모달 */}
      <Modal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        title="비밀번호 변경"
        size="md"
      >
        <form onSubmit={handlePasswordChange} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              현재 비밀번호
            </label>
            <input
              type="password"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="현재 비밀번호를 입력하세요"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              새 비밀번호
            </label>
            <input
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="새 비밀번호를 입력하세요"
              required
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              비밀번호는 6자 이상이어야 합니다.
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              새 비밀번호 확인
            </label>
            <input
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="새 비밀번호를 다시 입력하세요"
              required
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => setIsPasswordModalOpen(false)}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white transition-colors"
            >
              취소
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              변경
            </button>
          </div>
        </form>
      </Modal>

      {/* 회원탈퇴 확인 모달 */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="회원탈퇴 확인"
        size="md"
      >
        <div className="space-y-4">
          <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
              ⚠️ 회원탈퇴 경고
            </h3>
            <p className="text-red-700 dark:text-red-300">
              회원탈퇴를 진행하면 모든 데이터가 영구적으로 삭제되며, 복구할 수 없습니다.
            </p>
            <p className="text-red-700 dark:text-red-300 mt-2">
              정말로 회원탈퇴를 하시겠습니까?
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white transition-colors"
            >
              취소
            </button>
            <button
              onClick={handleAccountDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              회원탈퇴
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};